package com.schoolconnect.app.feature.support

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.TicketListItemResponse
import com.schoolconnect.app.datasource.network.response.TicketResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.itusers.ITUsersRepository
import com.schoolconnect.app.repository.school.SchoolRepository
import com.schoolconnect.app.repository.support.SupportQueueRepository
import io.ktor.http.ContentType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime

data class SupportQueueTicketUi(
    val id: String,
    val ticketId: String,
    val schoolId: String,
    val schoolName: String,
    val requesterName: String,
    val requesterRole: String,
    val subject: String,
    val status: String,
    val assignedToId: String,
    val assignedToName: String,
    val createdAt: String,
    val updatedAt: String
)

data class SupportAssigneeOption(
    val userId: String,
    val name: String
)

data class SupportSchoolOption(
    val id: String,
    val name: String
)

data class SupportQueueConversationMessageUi(
    val senderName: String,
    val message: String,
    val createdAt: String
)

data class SupportQueueTicketDetailUi(
    val id: String,
    val ticketId: String,
    val schoolName: String,
    val requesterName: String,
    val requesterRole: String,
    val subject: String,
    val description: String,
    val status: String,
    val assignedToId: String,
    val assignedToName: String,
    val createdAt: String,
    val updatedAt: String,
    val conversation: List<SupportQueueConversationMessageUi>
)

data class SupportQueueUiState(
    val query: String = "",
    val schoolId: String = "",
    val requesterRole: String = "",
    val status: String = "",
    val periodRange: String = "All Time",
    val sortBy: String = "updated_at",
    val sortAscending: Boolean = false,
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val tickets: List<SupportQueueTicketUi> = emptyList(),
    val totalCount: Int = 0,
    val isLoading: Boolean = false,
    val errorMessage: String = "",
    val schools: List<SupportSchoolOption> = emptyList(),
    val assignees: List<SupportAssigneeOption> = emptyList(),
    val isLookupsLoading: Boolean = false,
    val showRaiseDialog: Boolean = false,
    val isRaiseSaving: Boolean = false,
    val raiseErrorMessage: String = "",
    val viewTicketId: String? = null,
    val editTicketId: String? = null,
    val selectedDetail: SupportQueueTicketDetailUi? = null,
    val isDetailLoading: Boolean = false,
    val detailErrorMessage: String = "",
    val isEditSaving: Boolean = false,
    val editErrorMessage: String = "",
    val isReplying: Boolean = false,
    val replyErrorMessage: String = "",
    val replyGeneration: Int = 0
)

class SupportQueueViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var detailJob: Job? = null
    private val _uiState = MutableStateFlow(SupportQueueUiState(isLoading = true, isLookupsLoading = true))
    val uiState: StateFlow<SupportQueueUiState> = _uiState.asStateFlow()

    init {
        loadLookups()
        loadPage()
    }

    fun searchChanged(value: String) {
        _uiState.update { it.copy(query = value, pageIndex = 0) }
        loadPage(true)
    }

    fun schoolChanged(value: String) {
        _uiState.update { it.copy(schoolId = value, pageIndex = 0) }
        loadPage(true)
    }

    fun roleChanged(value: String) {
        _uiState.update { it.copy(requesterRole = value, pageIndex = 0) }
        loadPage(true)
    }

    fun statusChanged(value: String) {
        _uiState.update { it.copy(status = value, pageIndex = 0) }
        loadPage(true)
    }

    fun periodChanged(value: String) {
        _uiState.update { it.copy(periodRange = value, pageIndex = 0) }
        loadPage(true)
    }

    fun sortChanged(sortBy: String, ascending: Boolean) {
        _uiState.update { it.copy(sortBy = sortBy, sortAscending = ascending, pageIndex = 0) }
        loadPage(true)
    }

    fun pageChanged(value: Int) {
        _uiState.update { it.copy(pageIndex = value.coerceAtLeast(0)) }
        loadPage()
    }

    fun pageSizeChanged(value: Int) {
        val size = value.takeIf { it in PaginationPolicy.PAGE_SIZE_OPTIONS } ?: PaginationPolicy.DEFAULT_PAGE_SIZE
        _uiState.update { it.copy(pageSize = size, pageIndex = 0) }
        loadPage()
    }

    fun retry() = loadPage(true)

    fun clearFilters() {
        _uiState.update {
            it.copy(
                query = "",
                schoolId = "",
                requesterRole = "",
                status = "",
                periodRange = "All Time",
                pageIndex = 0
            )
        }
        loadPage(true)
    }

    fun openRaiseDialog() {
        _uiState.update { it.copy(showRaiseDialog = true, raiseErrorMessage = "", isRaiseSaving = false) }
    }

    fun dismissRaiseDialog() {
        if (_uiState.value.isRaiseSaving) return
        _uiState.update { it.copy(showRaiseDialog = false, raiseErrorMessage = "") }
    }

    fun raiseTicket(
        schoolId: String,
        subject: String,
        description: String,
        assigneeId: String?,
        attachmentName: String?,
        attachmentBytes: ByteArray?,
        attachmentContentType: ContentType?
    ) {
        if (_uiState.value.isRaiseSaving) return
        _uiState.update { it.copy(isRaiseSaving = true, raiseErrorMessage = "") }
        scope.launch {
            KtorApiClient.createSupportQueueTicket(
                schoolId = schoolId,
                subject = subject,
                description = description,
                assigneeId = assigneeId,
                attachmentName = attachmentName,
                attachmentBytes = attachmentBytes,
                attachmentContentType = attachmentContentType
            ).onSuccess {
                _uiState.update { it.copy(isRaiseSaving = false, showRaiseDialog = false, raiseErrorMessage = "") }
                loadPage(true)
            }.onFailure { error ->
                _uiState.update {
                    it.copy(
                        isRaiseSaving = false,
                        raiseErrorMessage = error.message.orEmpty().ifBlank { "Unable to raise the support ticket." }
                    )
                }
            }
        }
    }

    fun openViewTicket(ticketId: String) {
        val seed = _uiState.value.tickets.firstOrNull { it.id == ticketId }?.toDetailSeed()
        _uiState.update {
            it.copy(
                viewTicketId = ticketId,
                editTicketId = null,
                selectedDetail = seed,
                detailErrorMessage = "",
                replyErrorMessage = "",
                editErrorMessage = ""
            )
        }
        loadDetail(ticketId)
    }

    fun openEditTicket(ticketId: String) {
        val seed = _uiState.value.tickets.firstOrNull { it.id == ticketId }?.toDetailSeed()
        _uiState.update {
            it.copy(
                editTicketId = ticketId,
                viewTicketId = null,
                selectedDetail = seed,
                detailErrorMessage = "",
                replyErrorMessage = "",
                editErrorMessage = ""
            )
        }
        loadDetail(ticketId)
    }

    fun dismissTicketDialog() {
        if (_uiState.value.isEditSaving || _uiState.value.isReplying) return
        detailJob?.cancel()
        _uiState.update {
            it.copy(
                viewTicketId = null,
                editTicketId = null,
                selectedDetail = null,
                isDetailLoading = false,
                detailErrorMessage = "",
                editErrorMessage = "",
                replyErrorMessage = ""
            )
        }
    }

    fun retryDetail() {
        val id = _uiState.value.viewTicketId ?: _uiState.value.editTicketId ?: return
        loadDetail(id)
    }

    fun saveTicketEdits(statusDisplay: String, assigneeUserId: String?) {
        val detail = _uiState.value.selectedDetail ?: return
        if (_uiState.value.isEditSaving) return
        _uiState.update { it.copy(isEditSaving = true, editErrorMessage = "") }
        scope.launch {
            KtorApiClient.updateSupportQueueTicketStatusAssignment(
                ticketId = detail.id,
                status = statusDisplay,
                assigneeId = assigneeUserId
            ).onSuccess {
                _uiState.update { it.copy(isEditSaving = false, editErrorMessage = "") }
                loadDetail(detail.id)
                loadPage(true)
            }.onFailure { error ->
                _uiState.update {
                    it.copy(
                        isEditSaving = false,
                        editErrorMessage = error.message.orEmpty().ifBlank { "Unable to update the ticket." }
                    )
                }
            }
        }
    }

    fun replyToTicket(
        message: String,
        attachmentName: String?,
        attachmentBytes: ByteArray?,
        attachmentContentType: ContentType?
    ) {
        val detail = _uiState.value.selectedDetail ?: return
        if (_uiState.value.isReplying) return
        _uiState.update { it.copy(isReplying = true, replyErrorMessage = "") }
        scope.launch {
            KtorApiClient.replyToSupportQueueTicket(
                ticketId = detail.id,
                message = message,
                attachmentName = attachmentName,
                attachmentBytes = attachmentBytes,
                attachmentContentType = attachmentContentType
            ).onSuccess {
                _uiState.update {
                    it.copy(isReplying = false, replyErrorMessage = "", replyGeneration = it.replyGeneration + 1)
                }
                loadDetail(detail.id)
                loadPage(true)
            }.onFailure { error ->
                _uiState.update {
                    it.copy(
                        isReplying = false,
                        replyErrorMessage = error.message.orEmpty().ifBlank { "Unable to send the reply." }
                    )
                }
            }
        }
    }

    fun clear() {
        loadJob?.cancel()
        detailJob?.cancel()
        scope.cancel()
    }

    private fun loadLookups() {
        scope.launch {
            val schoolsResult = SchoolRepository.loadSchools(isActive = true, forceRemote = false)
            val assigneesResult = ITUsersRepository.loadItUsersPage(
                isActive = true,
                pageIndex = 0,
                pageSize = 100,
                forceRemote = false
            )
            val schools = schoolsResult.getOrNull().orEmpty().mapNotNull { school ->
                val id = school.id?.trim().orEmpty()
                val name = school.name?.trim().orEmpty()
                if (id.isBlank() || name.isBlank()) null else SupportSchoolOption(id, name)
            }.sortedBy { it.name.lowercase() }
            val assignees = assigneesResult.getOrNull()?.users.orEmpty().mapNotNull { user ->
                val userId = user.userId?.trim().orEmpty().ifBlank { user.id?.trim().orEmpty() }
                val name = user.name?.trim().orEmpty().ifBlank { user.email?.trim().orEmpty() }
                if (userId.isBlank() || name.isBlank()) null else SupportAssigneeOption(userId, name)
            }.distinctBy { it.userId }.sortedBy { it.name.lowercase() }
            _uiState.update {
                it.copy(
                    schools = schools,
                    assignees = assignees,
                    isLookupsLoading = false
                )
            }
        }
    }

    private fun loadPage(forceRemote: Boolean = false) {
        loadJob?.cancel()
        val requested = _uiState.value
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        loadJob = scope.launch {
            SupportQueueRepository.loadPage(
                query = requested.query,
                schoolId = requested.schoolId,
                requesterRole = requested.requesterRole,
                status = requested.status,
                periodStart = requested.periodRange.toPeriodStart(),
                pageIndex = requested.pageIndex,
                pageSize = requested.pageSize,
                sortBy = requested.sortBy,
                order = if (requested.sortAscending) "asc" else "desc",
                forceRemote = forceRemote,
                refreshScope = scope
            ).onSuccess { items ->
                _uiState.update {
                    it.copy(
                        tickets = items.map(TicketListItemResponse::toUi),
                        totalCount = items.serverTotalCountOrNull() ?: items.size,
                        isLoading = false,
                        errorMessage = ""
                    )
                }
            }.onFailure { error ->
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = error.message.orEmpty().ifBlank { "Unable to load the support queue." }
                    )
                }
            }
        }
    }

    private fun loadDetail(ticketId: String) {
        detailJob?.cancel()
        // Keep any list-row seed visible while the full detail request completes.
        _uiState.update { it.copy(isDetailLoading = true, detailErrorMessage = "") }
        detailJob = scope.launch {
            KtorApiClient.getSupportQueueTicketDetails(ticketId)
                .onSuccess { detail ->
                    _uiState.update {
                        it.copy(
                            selectedDetail = detail.toDetailUi(),
                            isDetailLoading = false,
                            detailErrorMessage = ""
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isDetailLoading = false,
                            detailErrorMessage = error.message.orEmpty().ifBlank { "Unable to load ticket details." }
                        )
                    }
                }
        }
    }
}

private fun String.toPeriodStart(): String? = when (this) {
    "Today" -> {
        val zone = TimeZone.currentSystemDefault()
        Clock.System.now().toLocalDateTime(zone).date.atStartOfDayIn(zone).toString()
    }
    "Last 7 Days" -> Clock.System.now().minus(7.days).toString()
    "Last 30 Days" -> Clock.System.now().minus(30.days).toString()
    "Last 90 Days" -> Clock.System.now().minus(90.days).toString()
    else -> null
}

private fun TicketListItemResponse.toUi(): SupportQueueTicketUi {
    val backendId = listOf(id, ticketId).firstOrNull { value -> value.looksLikeUuid() }
        ?: id.orEmpty().ifBlank { ticketId.orEmpty() }
    val displayTicketId = ticketId.orEmpty()
        .takeUnless { value -> value.looksLikeUuid() }
        .orEmpty()
        .ifBlank { id.orEmpty() }
    return SupportQueueTicketUi(
        id = backendId,
        ticketId = displayTicketId,
        schoolId = schoolId.orEmpty(),
        schoolName = schoolName.orEmpty().ifBlank { "-" },
        requesterName = requesterName.orEmpty().ifBlank { "-" },
        requesterRole = requesterRole.orEmpty().ifBlank { "-" },
        subject = subject.orEmpty().ifBlank { "-" },
        status = status.orEmpty(),
        assignedToId = assignedToId.orEmpty(),
        assignedToName = assignedToName.orEmpty().ifBlank { "Unassigned" },
        createdAt = createdAt.orEmpty(),
        updatedAt = updatedAt.orEmpty()
    )
}

private fun SupportQueueTicketUi.toDetailSeed(): SupportQueueTicketDetailUi = SupportQueueTicketDetailUi(
    id = id,
    ticketId = ticketId.ifBlank { id },
    schoolName = schoolName,
    requesterName = requesterName,
    requesterRole = requesterRole,
    subject = subject,
    description = "",
    status = status,
    assignedToId = assignedToId,
    assignedToName = assignedToName,
    createdAt = createdAt,
    updatedAt = updatedAt.ifBlank { createdAt },
    conversation = emptyList()
)

private fun TicketResponse.toDetailUi(): SupportQueueTicketDetailUi {
    val backendId = id.orEmpty().ifBlank { ticketId.orEmpty() }
    return SupportQueueTicketDetailUi(
        id = backendId,
        ticketId = ticketId.orEmpty().ifBlank { backendId },
        schoolName = schoolName.orEmpty().ifBlank { "-" },
        requesterName = requesterName.orEmpty().ifBlank { "User" },
        requesterRole = requesterRole.orEmpty().ifBlank { "-" },
        subject = subject.orEmpty().ifBlank { "-" },
        description = description.orEmpty(),
        status = status.orEmpty(),
        assignedToId = assignedToId.orEmpty(),
        assignedToName = assignedToName.orEmpty().ifBlank { "Unassigned" },
        createdAt = createdAt.orEmpty(),
        updatedAt = updatedAt.orEmpty().ifBlank { createdAt.orEmpty() },
        conversation = messages.orEmpty().map { message ->
            SupportQueueConversationMessageUi(
                senderName = message.senderName.orEmpty().ifBlank { "User" },
                message = message.message.orEmpty(),
                createdAt = message.createdAt.orEmpty()
            )
        }
    )
}

private fun String?.looksLikeUuid(): Boolean = this.orEmpty().matches(
    Regex("^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$")
)
